import type { AdminKpisTrendLinePanelProps, AdminKpisTrendWindow } from "../../models/admin-kpis.models";
import {
  ADMIN_KPIS_TREND_CHART_HEIGHT,
  ADMIN_KPIS_TREND_CHART_WIDTH,
  ADMIN_KPIS_TREND_MIN_CHART_WIDTH,
  ADMIN_KPIS_TREND_PLOT_BOTTOM,
  ADMIN_KPIS_TREND_PLOT_LEFT,
  ADMIN_KPIS_TREND_PLOT_RIGHT,
  ADMIN_KPIS_TREND_PLOT_TOP,
  ADMIN_KPIS_TREND_WINDOW_OPTIONS,
  buildAdminKpisTrendChartData,
  formatAdminKpisTrendDate,
  formatAdminKpisTrendPointTitle,
  getAdminKpisPressedButtonAriaProps,
  getAdminKpisTrendColorClassName,
  getAdminKpisTrendDateLabelClassName,
  getAdminKpisTrendPoints,
  getAdminKpisTrendSegment,
} from "../../utils/admin-kpis.utils";
import "./AdminKpisPie.css";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const AdminKpisTrendLinePanel = ({
  activeFilter,
  history,
  periodFleetCertificateStatus,
  periodFleetBalticQuestionnaireStatus,
  selectedWindow,
  onWindowChange,
}: AdminKpisTrendLinePanelProps) => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(ADMIN_KPIS_TREND_CHART_WIDTH);
  const selectedSegment = useMemo(
    () => getAdminKpisTrendSegment(activeFilter, periodFleetCertificateStatus, periodFleetBalticQuestionnaireStatus),
    [activeFilter, periodFleetBalticQuestionnaireStatus, periodFleetCertificateStatus],
  );
  const trendPoints = useMemo(
    () => getAdminKpisTrendPoints(history, activeFilter, selectedWindow),
    [activeFilter, history, selectedWindow],
  );
  const chartData = useMemo(
    () => buildAdminKpisTrendChartData(trendPoints, chartWidth, selectedWindow),
    [chartWidth, selectedWindow, trendPoints],
  );
  const hasTrendPoints = chartData.points.length > 0;

  useEffect(() => {
    if (!hasTrendPoints) {
      return undefined;
    }

    const chartContainer = chartContainerRef.current;
    if (!chartContainer) {
      return undefined;
    }

    const updateChartWidth = (containerWidth: number) => {
      const nextChartWidth = Math.max(Math.round(containerWidth), ADMIN_KPIS_TREND_MIN_CHART_WIDTH);
      setChartWidth((currentChartWidth) => (currentChartWidth === nextChartWidth ? currentChartWidth : nextChartWidth));
    };
    const measureChartWidth = () => updateChartWidth(chartContainer.getBoundingClientRect().width);

    measureChartWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureChartWidth);

      return () => window.removeEventListener("resize", measureChartWidth);
    }

    const resizeObserver = new ResizeObserver((entries) => {
      updateChartWidth(entries[0]?.contentRect.width ?? chartContainer.getBoundingClientRect().width);
    });
    resizeObserver.observe(chartContainer);

    return () => resizeObserver.disconnect();
  }, [hasTrendPoints]);

  if (!activeFilter) {
    return null;
  }

  const selectedMetricLabel = selectedSegment?.label ?? activeFilter.code;
  const trendColorClassName = getAdminKpisTrendColorClassName(selectedSegment?.code ?? activeFilter.code);
  const selectedWindowLabel =
    ADMIN_KPIS_TREND_WINDOW_OPTIONS.find((option) => option.value === selectedWindow)?.label ?? "30 days";
  const gridLineStart = ADMIN_KPIS_TREND_PLOT_LEFT;
  const gridLineEnd = chartData.chartWidth - ADMIN_KPIS_TREND_PLOT_RIGHT;
  const plotBottom = ADMIN_KPIS_TREND_CHART_HEIGHT - ADMIN_KPIS_TREND_PLOT_BOTTOM;
  const plotMiddle = ADMIN_KPIS_TREND_PLOT_TOP + (plotBottom - ADMIN_KPIS_TREND_PLOT_TOP) / 2;

  return (
    <section
      data-test="admin-kpis-trendline-section"
      className={twMerge(
        "min-w-0 border border-[#D7E2DE] bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800",
        trendColorClassName,
      )}
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold">Historical Trendline</h2>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-sm text-[#4A5655] dark:text-gray-300">
            <span className="admin-kpis-trend-swatch h-3 w-3 flex-none rounded-sm" aria-hidden="true" />
            <span className="min-w-0 break-words">{selectedMetricLabel}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" aria-label="Trendline period">
          {ADMIN_KPIS_TREND_WINDOW_OPTIONS.map((option) => {
            const isSelected = selectedWindow === option.value;
            const buttonClassName = twMerge(
              "border border-[#D7E2DE] px-3 py-1.5 text-xs font-semibold transition-colors dark:border-gray-700",
              isSelected
                ? "bg-green text-white"
                : "bg-white text-[#21312F] hover:bg-[#F2F7F3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#367C2B] dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700",
            );
            const handleWindowClick = () => onWindowChange(option.value as AdminKpisTrendWindow);

            return (
              <button
                key={option.value}
                type="button"
                {...getAdminKpisPressedButtonAriaProps(isSelected)}
                className={buttonClassName}
                onClick={handleWindowClick}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {hasTrendPoints ? (
        <div>
          <div ref={chartContainerRef} className="min-w-0">
            <svg
              className="admin-kpis-trend-chart"
              viewBox={`0 0 ${chartData.chartWidth} ${ADMIN_KPIS_TREND_CHART_HEIGHT}`}
              role="img"
              aria-label={`${selectedMetricLabel} trendline for ${selectedWindowLabel}`}
            >
              <line
                className="admin-kpis-trend-grid-line"
                x1={gridLineStart}
                x2={gridLineEnd}
                y1={ADMIN_KPIS_TREND_PLOT_TOP}
                y2={ADMIN_KPIS_TREND_PLOT_TOP}
              />
              <line
                className="admin-kpis-trend-grid-line"
                x1={gridLineStart}
                x2={gridLineEnd}
                y1={plotMiddle}
                y2={plotMiddle}
              />
              <line
                className="admin-kpis-trend-axis-line"
                x1={gridLineStart}
                x2={gridLineEnd}
                y1={plotBottom}
                y2={plotBottom}
              />
              <text className="admin-kpis-trend-axis-label" x={8} y={ADMIN_KPIS_TREND_PLOT_TOP + 4}>
                {chartData.maxCount}
              </text>
              <text className="admin-kpis-trend-axis-label" x={8} y={plotMiddle + 4}>
                {chartData.midpointCount}
              </text>
              <text className="admin-kpis-trend-axis-label" x={8} y={plotBottom + 4}>
                0
              </text>
              <path className="admin-kpis-trend-line" d={chartData.pathData} />
              {chartData.tooltipPoints.map((point) => (
                <circle key={point.snapshotDate} className="admin-kpis-trend-point" cx={point.x} cy={point.y} r={4}>
                  <title>{formatAdminKpisTrendPointTitle(point)}</title>
                </circle>
              ))}
              {chartData.dateAxisPoints.map((point, index) => (
                <text
                  key={point.snapshotDate}
                  className={getAdminKpisTrendDateLabelClassName(index, chartData.dateAxisPoints.length)}
                  x={point.x}
                  y={ADMIN_KPIS_TREND_CHART_HEIGHT - 12}
                >
                  {formatAdminKpisTrendDate(point.snapshotDate)}
                </text>
              ))}
            </svg>
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-[#D7E2DE] px-4 py-6 text-sm text-[#4A5655] dark:border-gray-700 dark:text-gray-300">
          No daily snapshot history is available for {selectedMetricLabel} in this period.
        </div>
      )}
    </section>
  );
};

export default memo(AdminKpisTrendLinePanel);
